import * as Yup from 'yup';
import { Formik } from 'formik';
import { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  FiCreditCard,
  FiFile,
  FiMapPin,
  FiShoppingCart,
  FiUser,
} from 'react-icons/fi';

import { parseCookies } from 'nookies';
import { Box, TextField } from '@mui/material';
import { Headline } from '../../components/LegacyV4Components';
import v4CompanyLogo from '../../assets/logos/v4-company-logo.svg';
import { AboutYou } from '../../components/AtomicDesign/templates/AboutYou';
import { MethodPayment } from '../../components/AtomicDesign/templates/MethodPayment';
import { ResumePayment } from '../../components/AtomicDesign/templates/ResumePayment';
import { AddressPayment } from '../../components/AtomicDesign/templates/AddressPayment';
import { convertNumberToBRL } from '../../utils/convertNumberToBRL';
import { CustomerFinishPayment } from '../../components/AtomicDesign/templates/CustomerFinishPayment';
import { createPaymentProps } from '../../services/requests/Finance/createPayment';

import * as S from './styles';
import { customerApi, financeApi } from '../../services/api';
import { useQueryGetCustomerById } from '../../services/requests/Customers/getCustomerById';
import { ModalCookies } from '../../components/AtomicDesign/molecules/ModalCookies';
import { ModalAcceptedCookies } from '../../components/AtomicDesign/molecules/ModalAcceptedCookies';
import { useGetCheckoutById } from '../../services/requests/Finance/getCheckoutById';
import { ValidateCoupon } from '../../types/coupons';
import { useToast } from '../../hooks/toast';
import { ValidateCPFOrCNPJ } from '../../utils/validateCPFOrCNPJ';
import { UnHappyPath } from '../../components/AtomicDesign/atoms/UnhappyPath';

export interface FormikPaymentOptions {
  recipientId?: string;
  ticketUnitId?: string;
  quantity?: number;
  paymentMethod: string;
  name: string;
  email: string;
  telephone?: string;
  finalValue?: number;
  couponApplied?: string;
  discount?: number;
  cpf?: string;
  address: {
    district: string;
    number: null;
    street: string;
    zipCode?: string;
    zip_code?: string;
    complement: string;
  };
  cardName?: string;
  cardNumber?: string;
  monthAvailable?: string;
  yearAvailable?: string;
  cvv?: string;
  parcelas?: string;
  cpf_cnpj?: string;
  phone?: string;
}

export function PaymentOptions() {
  const [isLoading, setIsLoading] = useState(false);
  const [indexTab, setIndexTab] = useState<number>(1);
  const [customerAvatar, setCustomerAvatar] = useState('');
  const [cupomData, setCupomData] = useState({} as ValidateCoupon);
  const [couponSelect, setCouponSelect] = useState('');
  const [paymentIsCreate, setPaymentIsCreate] = useState(false);
  const [selectMethod, setSelectMethod] = useState('');
  const [isOpenModal, setIsOpenModal] = useState(true);
  const [isCookiesAccepted, setIsCookiesAccepted] = useState<string>();
  const [responsePayment, setResponsePayment] = useState(
    {} as createPaymentProps,
  );
  const [searchParams] = useSearchParams();
  const { addToast } = useToast();
  const isMatriz = searchParams.get('isMatriz');
  const { id } = useParams<{ id: string }>();
  const { data: product } = useGetCheckoutById(id);
  const configBankSchema = Yup.object().shape({
    name: Yup.string().required('O campo é obrigatório!'),
    email: Yup.string()
      .email('E-mail inválido')
      .required('Formato de e-mail inválido.'),
    telephone: Yup.string()
      .min(11, 'Número inválido')
      .required('Insira seu número'),
    cpf: Yup.string()
      .max(14, 'CPF inválido')
      .required('Insira seu cpf')
      .test((value) => ValidateCPFOrCNPJ(value))
      .nullable(),
    cardName: Yup.number()
      .max(70, 'O valor da porcentagem de comissão não pode ser maior que 70%')
      .required('O campo é obrigatório!'),
    paymentMethod: Yup.array().required('O campo é obrigatório!'),
    cardNumber: Yup.string().required('O campo é obrigatório!'),
    monthAvailable: Yup.string().required('O campo é obrigatório!'),
    parcelas: Yup.string(),
    cvv: Yup.string(),
    address: Yup.object().shape({
      district: Yup.string().required('O campo é obrigatório!'),
      number: Yup.number().required('O campo é obrigatório'),
      street: Yup.string().required('O campo é obrigatório!'),
      zipCode: Yup.string().required('O campo é obrigatório!'),
      complement: Yup.string().required('O campo é obrigatório!'),
    }),
  });

  const handleGetCustomerAvatar = useCallback(async () => {
    const avatar = await customerApi.get<{ avatar: { url: string } }>(
      `/customerUnit/avatar/${product?.data.recipientId}`,
    );

    setCustomerAvatar(avatar.data.avatar.url);
  }, [product?.data.recipientId]);

  const handleGetCupom = useCallback(async () => {
    try {
      const cupom = await financeApi.post<ValidateCoupon>(`/coupons/validate`, {
        name: couponSelect,
        checkoutId: id,
      });

      setCupomData(cupom as unknown as ValidateCoupon);
    } catch (error) {
      addToast({
        title: `${error?.response?.data?.message}`,
        type: 'error',
      });
    }
  }, [couponSelect, id, addToast]);

  const { data: customerById } = useQueryGetCustomerById(
    product?.data?.recipientId,
  );

  const customerHexColor = customerById?.data?.hexColor || 'var(--success-2)';

  const isSalesEducation =
    product?.data.recipientId === '6408e591dc28c7a232ac476c';
  const isGMPCourse = product?.data?.name?.toUpperCase()?.includes('GMP');

  const useGMPTerms = isSalesEducation && isGMPCourse;

  useEffect(() => {
    handleGetCustomerAvatar();
  }, [handleGetCustomerAvatar]);
  useEffect(() => {
    const { 'v4company.cookiesAccepted': cookiesAccepted } = parseCookies();

    if (!cookiesAccepted) {
      setIsOpenModal(true);
      return;
    }
    setIsOpenModal(false);
    setIsCookiesAccepted(cookiesAccepted);
  }, []);

  return (
    <>
      <Formik
        initialValues={{
          recipientId: `${id}`,
          paymentMethod: 'pix',
          name: '',
          email: '',
          telephone: '',
          cpf: '',
          address: {
            district: '',
            number: null,
            street: '',
            zipCode: '',
            complement: '',
          },
        }}
        validationSchema={configBankSchema}
        onSubmit={async () => {
          setIndexTab(indexTab + 1);
        }}
      >
        {({ errors, values, handleBlur, setFieldValue, touched }) => (
          <Box>
            {product?.data?.disabled === true ? (
              <S.LinkDisabled>
                <Box className="ContainerDisabled">
                  <UnHappyPath
                    assetIndex={1}
                    title="Este link de pagamento está desabilitado ou não existe."
                  />
                </Box>
              </S.LinkDisabled>
            ) : (
              <>
                <S.Container>
                  {customerAvatar ? (
                    <S.Header>
                      <S.CustomerAvatar
                        alt={product?.data?.name}
                        src={customerAvatar}
                      />
                    </S.Header>
                  ) : (
                    ''
                  )}

                  <S.Navbar>
                    {indexTab !== 5 ? (
                      <>
                        <S.ContainerTabs>
                          <section
                            style={{
                              border: `${
                                indexTab === 1 &&
                                `1px solid ${customerHexColor}`
                              }`,
                              background: `${
                                indexTab === 1 ? `#fff` : `${customerHexColor}`
                              }`,
                            }}
                            aria-hidden
                            className={`${
                              (indexTab === 1 && `selected`) ||
                              (indexTab !== 1 && 'NotSelected')
                            }`}
                            key={1}
                            onClick={() => {
                              if (
                                values.name &&
                                !errors.name &&
                                !errors.email &&
                                values.email &&
                                values.cpf &&
                                !errors.cpf &&
                                values.telephone
                                // !errors.tel
                              ) {
                                indexTab === 1 && setIndexTab(1);
                                indexTab === 2 && setIndexTab(indexTab - 1);
                              }
                            }}
                          >
                            <FiUser
                              color={
                                indexTab === 1
                                  ? `${customerHexColor}`
                                  : '#333333'
                              }
                            />
                            <span>Sobre você</span>
                          </section>
                        </S.ContainerTabs>
                        <S.ContainerTabs>
                          <section
                            style={{
                              border: `${
                                indexTab === 2
                                  ? `1px solid ${customerHexColor}`
                                  : '1px solid #dadae7'
                              }`,
                              background: `${
                                (indexTab === 2 && `#fff`) ||
                                (indexTab > 2 && `${customerHexColor}`)
                              }`,
                            }}
                            aria-hidden
                            className={`${
                              (indexTab === 2 && `selected`) ||
                              (indexTab > 2 && 'NotSelected')
                            }`}
                            key={2}
                            onClick={() => {
                              if (
                                values.name &&
                                !errors.name &&
                                !errors.email &&
                                values.email &&
                                values.cpf &&
                                !errors.cpf &&
                                values.telephone
                                // !errors.tel
                              ) {
                                indexTab === 1 && setIndexTab(2);
                                indexTab === 3 && setIndexTab(indexTab - 1);
                              }
                            }}
                          >
                            <FiMapPin
                              color={
                                indexTab === 2 ? `${customerHexColor}` : ''
                              }
                            />
                            <span>Endereço de cobrança</span>
                          </section>
                        </S.ContainerTabs>
                        <S.ContainerTabs>
                          <section
                            style={{
                              border: `${
                                indexTab === 3
                                  ? `1px solid ${customerHexColor}`
                                  : '1px solid #dadae7'
                              }`,
                              background: `${
                                (indexTab === 3 && `#fff`) ||
                                (indexTab > 3 && `${customerHexColor}`)
                              }`,
                            }}
                            aria-hidden
                            className={`${
                              (indexTab === 3 && `selected`) ||
                              (indexTab > 3 && 'NotSelected')
                            }`}
                            key={3}
                            onClick={() => {
                              if (
                                !errors.address?.zipCode &&
                                !errors.address?.complement &&
                                !errors.address?.district &&
                                !errors.address?.number &&
                                !errors.address?.street
                              ) {
                                indexTab === 2 && setIndexTab(3);
                                indexTab === 4 && setIndexTab(indexTab - 1);
                              }
                            }}
                          >
                            <FiCreditCard
                              color={
                                indexTab === 3 ? `${customerHexColor}` : ''
                              }
                            />
                            <span>Pagamento</span>
                          </section>
                        </S.ContainerTabs>
                        <S.ContainerTabs>
                          <section
                            style={{
                              border: `${
                                indexTab === 4
                                  ? `1px solid ${customerHexColor}`
                                  : '1px solid #dadae7'
                              }`,
                              background: `${
                                indexTab > 4 && `${customerHexColor}`
                              }`,
                            }}
                            aria-hidden
                            className={`${
                              (indexTab === 4 && `selected`) ||
                              (indexTab > 4 && 'NotSelected')
                            }`}
                            key={4}
                            onClick={() => {
                              indexTab === 3 && setIndexTab(4);
                            }}
                          >
                            <FiFile
                              color={
                                indexTab === 4 ? `${customerHexColor}` : ''
                              }
                            />
                            <span>Resumo</span>
                          </section>
                        </S.ContainerTabs>
                      </>
                    ) : null}
                  </S.Navbar>

                  <S.Body theme={indexTab === 5 ? 'center' : 'space-between'}>
                    {indexTab !== 5 ? (
                      <S.RequestsMobile
                        color={indexTab === 4 ? 'none' : 'block'}
                        style={{ border: `1px solid ${customerHexColor}` }}
                      >
                        <div className="your-request">
                          <FiShoppingCart />
                          <Headline variant="h5">Seu pedido</Headline>
                        </div>
                        <section>
                          <div className="your-product">
                            <p>
                              {isMatriz === 'true'
                                ? product?.data?.products[0]?.name
                                : product?.data?.name || ''}
                            </p>
                            <p>
                              {product?.data
                                ? convertNumberToBRL(
                                    isMatriz
                                      ? product?.data?.products[0]?.value
                                      : product?.data?.value / 100,
                                  )
                                : ''}
                            </p>
                          </div>
                          <p>
                            {isMatriz === 'true'
                              ? product?.data?.products[0]?.description
                              : product?.data?.description}
                          </p>
                        </section>
                        {isMatriz === 'true' && (
                          <Box sx={{ display: 'flex', padding: '1rem' }}>
                            <TextField
                              label="Cupom de desconto"
                              value={couponSelect}
                              placeholder="EX: CUPOM10OFF"
                              onChange={(e) => setCouponSelect(e.target.value)}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  '&.Mui-focused fieldset': {
                                    borderColor: customerHexColor,
                                  },
                                },
                                '& label': {
                                  '&.Mui-focused': {
                                    color: '#7f7f7f',
                                  },
                                },
                              }}
                            />
                            <S.ButtonCoupons onClick={handleGetCupom}>
                              Aplicar
                            </S.ButtonCoupons>
                          </Box>
                        )}
                        {cupomData?.data && (
                          <>
                            {!cupomData?.data?.disable ? (
                              <>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    padding: '1rem',
                                  }}
                                >
                                  <p>Subtotal</p>{' '}
                                  <p>
                                    {product?.data &&
                                      convertNumberToBRL(
                                        product?.data?.products[0]?.value,
                                      )}
                                  </p>
                                </Box>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    padding: '0rem 1rem 0.5rem 1rem',
                                  }}
                                >
                                  {cupomData.data.discountType ===
                                  'percentage' ? (
                                    <p>Desconto de {cupomData.data.value}%</p>
                                  ) : (
                                    <p>Desconto</p>
                                  )}
                                  <p>
                                    {convertNumberToBRL(
                                      cupomData.data.discount,
                                    )}
                                  </p>
                                </Box>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    borderTop: '1px solid #BBBBBB',
                                    padding: '1rem',
                                    opacity: '0.8',
                                  }}
                                >
                                  <p>TOTAL</p>
                                  <p>
                                    {convertNumberToBRL(
                                      cupomData.data.finalValue,
                                    )}
                                  </p>
                                </Box>
                              </>
                            ) : (
                              <p>Cupom inválido ou desabilitado.</p>
                            )}
                          </>
                        )}
                      </S.RequestsMobile>
                    ) : null}

                    {indexTab === 1 && (
                      <AboutYou
                        touched={touched}
                        setFieldValue={setFieldValue}
                        indexTab={indexTab}
                        setIndexTab={setIndexTab}
                        values={values}
                        errors={errors}
                        handleBlur={handleBlur}
                      />
                    )}

                    {indexTab === 2 && (
                      <AddressPayment
                        touched={touched}
                        indexTab={indexTab}
                        setIndexTab={setIndexTab}
                        setFieldValue={setFieldValue}
                        values={values}
                        errors={errors}
                        handleBlur={handleBlur}
                      />
                    )}

                    {indexTab === 3 && (
                      <MethodPayment
                        indexTab={indexTab}
                        setIndexTab={setIndexTab}
                        setFieldValue={setFieldValue}
                        values={values}
                        selectMethod={selectMethod}
                        setSelectMethod={setSelectMethod}
                        product={product?.data || undefined}
                      />
                    )}
                    {indexTab === 4 && (
                      <ResumePayment
                        cupomDescount={cupomData?.data ?? null}
                        data={product?.data}
                        indexTab={indexTab}
                        setIndexTab={setIndexTab}
                        values={values}
                        setPaymentIsCreate={setPaymentIsCreate}
                        setResponsePayment={setResponsePayment}
                        setIsLoading={setIsLoading}
                        isLoading={isLoading}
                        selectMethod={selectMethod}
                        customerHexColor={customerHexColor}
                        useGMPTerms={useGMPTerms}
                      />
                    )}
                    {indexTab === 5 && paymentIsCreate ? (
                      <CustomerFinishPayment
                        responsePayment={responsePayment}
                        values={values}
                        couponDiscount={cupomData?.data ?? null}
                        data={product?.data}
                      />
                    ) : null}

                    {indexTab !== 5 ? (
                      <S.RequestsDesktop
                        color={indexTab === 4 ? 'none' : 'block'}
                        style={{ border: `1px solid ${customerHexColor}` }}
                      >
                        <div className="your-request">
                          <FiShoppingCart />
                          <Headline variant="h5">Seu pedido</Headline>
                        </div>
                        <section>
                          <div className="your-product">
                            <p>
                              {isMatriz === 'true'
                                ? product?.data?.products[0]?.name
                                : product?.data?.name || ''}
                            </p>
                            <p>
                              {product?.data
                                ? convertNumberToBRL(
                                    isMatriz
                                      ? product?.data?.products[0]?.value
                                      : product?.data?.value / 100,
                                  )
                                : ''}
                            </p>
                          </div>
                          <p>
                            {isMatriz === 'true'
                              ? product?.data?.products[0]?.description
                              : product?.data?.description}
                          </p>
                        </section>
                        {isMatriz === 'true' && (
                          <Box sx={{ display: 'flex', padding: '1rem' }}>
                            <TextField
                              label="Cupom de desconto"
                              value={couponSelect}
                              placeholder="EX: CUPOM10OFF"
                              onChange={(e) => setCouponSelect(e.target.value)}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  '&.Mui-focused fieldset': {
                                    borderColor: customerHexColor,
                                  },
                                },
                                '& label': {
                                  '&.Mui-focused': {
                                    color: '#7f7f7f',
                                  },
                                },
                              }}
                            />
                            <S.ButtonCoupons onClick={handleGetCupom}>
                              Aplicar
                            </S.ButtonCoupons>
                          </Box>
                        )}
                        {cupomData?.data && (
                          <>
                            {!cupomData?.data?.disable ? (
                              <>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    padding: '1rem',
                                  }}
                                >
                                  <p>Subtotal</p>{' '}
                                  <p>
                                    {product?.data &&
                                      convertNumberToBRL(
                                        product?.data?.products[0]?.value,
                                      )}
                                  </p>
                                </Box>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    padding: '0rem 1rem 0.5rem 1rem',
                                  }}
                                >
                                  {cupomData.data.discountType ===
                                  'percentage' ? (
                                    <p>Desconto de {cupomData.data.value}%</p>
                                  ) : (
                                    <p>Desconto</p>
                                  )}
                                  <p>
                                    {convertNumberToBRL(
                                      cupomData.data.discount,
                                    )}
                                  </p>
                                </Box>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    borderTop: '1px solid #BBBBBB',
                                    padding: '1rem',
                                    opacity: '0.8',
                                  }}
                                >
                                  <p>TOTAL</p>
                                  <p>
                                    {convertNumberToBRL(
                                      cupomData.data.finalValue,
                                    )}
                                  </p>
                                </Box>
                              </>
                            ) : (
                              <p>Cupom inválido ou desabilitado.</p>
                            )}
                          </>
                        )}
                      </S.RequestsDesktop>
                    ) : null}
                  </S.Body>

                  <S.Footer>
                    <p>
                      Desenvolvido por: <img src={v4CompanyLogo} alt="" />
                    </p>
                    <span>2022 © Todos os direitos reservados. V4 Company</span>
                  </S.Footer>
                </S.Container>
              </>
            )}
          </Box>
        )}
      </Formik>
      {!isOpenModal ? (
        <S.Cookie>
          <ModalCookies />
          {/* <BiCookie size={33} /> */}
        </S.Cookie>
      ) : null}
      {!isCookiesAccepted ? (
        <ModalAcceptedCookies
          show={isOpenModal}
          onClose={() => setIsOpenModal(false)}
        />
      ) : null}
    </>
  );
}
